import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, Calendar, AlertCircle } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ja } from "date-fns/locale";
import { useCalendar, useReleases } from "../../hooks/useApi";
import { ImpactBadge, SurpriseBadge, MarketViewBadge } from "../../components/Badge";
import { LoadingSpinner, ErrorMessage } from "../../components/LoadingSpinner";

export function Dashboard() {
  const recentReleases = useReleases({ size: 5 });
  const nextCalendar = useCalendar("next");
  const currentCalendar = useCalendar("current");

  const highImpactNext = nextCalendar.data?.events.filter((e) => e.impact === "high") ?? [];
  const highImpactCurrent = currentCalendar.data?.events.filter((e) => e.impact === "high") ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">マクロ経済モニター</h1>
        <span className="text-sm text-gray-500">
          {format(new Date(), "yyyy年M月d日（EEE）", { locale: ja })}
        </span>
      </div>

      {/* サマリーカード */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard
          title="今週のHIGH"
          value={`${highImpactCurrent.length}件`}
          icon={<AlertCircle className="text-red-500" size={20} />}
          loading={currentCalendar.isLoading}
        />
        <SummaryCard
          title="来週のHIGH"
          value={`${highImpactNext.length}件`}
          icon={<Calendar className="text-blue-500" size={20} />}
          loading={nextCalendar.isLoading}
        />
        <SummaryCard
          title="直近発表件数"
          value={`${recentReleases.data?.total ?? "-"}件`}
          icon={<TrendingUp className="text-green-500" size={20} />}
          loading={recentReleases.isLoading}
        />
      </div>

      {/* 直近の発表 */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-800">直近の発表</h2>
          <Link
            to="/releases"
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
          >
            すべて見る <ArrowRight size={14} />
          </Link>
        </div>
        {recentReleases.isLoading && <LoadingSpinner />}
        {recentReleases.isError && <ErrorMessage message="発表履歴の取得に失敗しました" />}
        {recentReleases.data && (
          <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
            {recentReleases.data.items.length === 0 && (
              <p className="py-8 text-center text-gray-500 text-sm">発表データがありません</p>
            )}
            {recentReleases.data.items.map((r) => (
              <Link
                key={r.id}
                to={`/releases/${r.id}`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-gray-900 truncate">{r.indicator.name}</span>
                    {r.analysis && <ImpactBadge level={r.analysis.impact_level} />}
                    {r.surprise_direction && <SurpriseBadge direction={r.surprise_direction} />}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {format(parseISO(r.released_at), "M/d HH:mm")} ·{" "}
                    実績 {r.result}{r.indicator.unit}
                    {r.forecast != null && ` / 予想 ${r.forecast}${r.indicator.unit}`}
                  </div>
                </div>
                {r.analysis && (
                  <MarketViewBadge view={r.analysis.market_view} />
                )}
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* 来週の HIGH 指標 */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-gray-800">来週の注目指標</h2>
          <Link
            to="/calendar?week=next"
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
          >
            カレンダー <ArrowRight size={14} />
          </Link>
        </div>
        {nextCalendar.isLoading && <LoadingSpinner />}
        {nextCalendar.isError && (
          <p className="text-sm text-gray-500">カレンダーは現在ご利用いただけません（無料プランの制約）</p>
        )}
        {highImpactNext.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
            {highImpactNext.map((e, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <ImpactBadge level="HIGH" />
                <div className="flex-1">
                  <div className="font-medium text-gray-900">{e.event}</div>
                  <div className="text-xs text-gray-500">
                    {e.country} · {format(parseISO(e.release_time), "M月d日(EEE) HH:mm", { locale: ja })}
                    {e.estimate != null && ` · 予想 ${e.estimate}${e.unit}`}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {!nextCalendar.isLoading && highImpactNext.length === 0 && (
          <p className="text-sm text-gray-500">来週の HIGH 指標はありません</p>
        )}
      </section>
    </div>
  );
}

function SummaryCard({
  title,
  value,
  icon,
  loading,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  loading: boolean;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 px-4 py-4 flex items-center gap-3">
      <div className="p-2 rounded-full bg-gray-50">{icon}</div>
      <div>
        <div className="text-sm text-gray-500">{title}</div>
        <div className="text-xl font-bold text-gray-900">
          {loading ? <span className="text-gray-300">---</span> : value}
        </div>
      </div>
    </div>
  );
}
